import type { RsvpCopy, SupportedMarketingLocale } from "./types";

export const rsvp: Record<SupportedMarketingLocale, RsvpCopy> = {
  en: {
    seo: {
      title: "RSVP that works in WhatsApp — Vote.GameOn",
      description:
        "Friction-free game RSVP for grassroots coaches. No app, no login, lives inside WhatsApp & Telegram. Privacy-first, co-parent ready.",
    },
    srHeading: "RSVP that works in WhatsApp — no app, no login",
    hero: {
      eyebrow: "The friction-free RSVP",
      headline: "RSVP that actually works.",
      headlineHighlight: "No app. No login. No nagging.",
      subhead:
        "WhatsApp-native. Paste one link, get answers in seconds. Built for grassroots coaches, parents, and players who just want game day to work.",
      cta: "Start a free team",
      seeHow: "See how it works",
      subLinks: {
        vote: "See Player's Player Voting →",
        rsvp: "See how RSVP works →",
        rpe: "See how Exertion tracking works →",
        feedback: "See how Coach Feedback works →",
      },
      responded: "14 of 16 responded",
      worksWith: "Shares to your team's chat app",
    },
    problem: {
      eyebrow: "Sound familiar?",
      cards: [
        { title: "Replies lost in the group chat", body: "You scroll past 200 memes to count yeses." },
        { title: "Apps no parent will install", body: "Yet another login. Yet another forgotten password." },
        { title: "11pm follow-ups", body: "Coaches chasing 'Are you coming Saturday?' on a school night." },
      ],
    },
    howItWorks: {
      heading: "Three steps. Zero friction.",
      subhead: "From coach creating the game to every parent responding — under a minute.",
      steps: [
        { badge: "10 seconds", title: "Coach creates a game", body: "Opponent, kickoff, location. The link is generated automatically." },
        { badge: "1 message", title: "Paste into WhatsApp or Telegram", body: "Rich preview unfurls with the team name, time and tap-to-RSVP CTA." },
        { badge: "1 tap", title: "Players tap Going / Not Going", body: "No account. No download. Just a tap. Coach sees results in real time." },
      ],
    },
    flowVisual: {
      heading: "The whole flow, on one screen",
      subhead: "From chat message to confirmed RSVP — every step the parent sees.",
      matchTime: "Saturday, 9:00 AM",
      matchTitle: "Lions U12 vs Eagles",
      venue: "Riverside Park, Field 3",
      weather: "18°C, partly cloudy",
      addCalendar: "Add to Calendar",
      going: "Going",
      notGoing: "Not Going",
      noAccount: "No account needed",
      successTitle: "You're in! 🎉",
      successBody: "Coach Sam can see your reply. We'll remind you the day before.",
      successCalendar: "✅ Added to your calendar",
      successCoParent: "✅ Co-parent notified",
    },
    whatsApp: {
      coachName: "Coach Sam",
      coachMessage: "Game on Saturday — please RSVP 🙏",
      previewTitle: "Lions U12 vs Eagles — Sat 9:00am",
      previewDescription: "Tap to RSVP. No login needed.",
      teamChat: "Team chat • now",
      parentReply: "Done ✅",
    },
    features: {
      heading: "Designed for the way teams actually communicate",
      subhead: "Six small details that add up to one big difference.",
      cards: [
        { title: "Zero accounts for players", body: "No signup wall. Tap the link, RSVP, done. Parents share via family chats." },
        { title: "Lives inside WhatsApp & Telegram", body: "Rich link previews unfurl with kickoff time, opponent and venue — without leaving the chat." },
        { title: "Privacy-first by design", body: "No contact upload. No tracking pixels. Parents control who can RSVP for their child." },
        { title: "Live weather + directions", body: "Open-Meteo forecast for kickoff and one-tap Google Maps navigation, baked into every RSVP card." },
        { title: "Add to Calendar", body: "Google Calendar, .ics download, or a live parent calendar feed that updates as games change." },
        { title: "Co-parent friendly", body: "Up to two guardians per child. Either can RSVP — invite the other parent in one tap." },
      ],
    },
    safeguards: {
      eyebrow: "Safeguards",
      heading: "Friction-free ",
      headingHighlight: "never",
      headingTail: " means careless.",
      cards: [
        { title: "Locked at kickoff", body: "Once the match starts, RSVPs freeze. Full audit trail kept for the coach." },
        { title: "Managed by parents", body: "Once a child has 2 active guardians, only they can RSVP. Strangers are gracefully blocked." },
        { title: "Time Off blocks", body: "Parents mark holidays or injury once — affected games auto-RSVP Not Going. No follow-up needed." },
      ],
    },
    comparison: {
      heading: "Why coaches switch",
      subhead: "What makes Vote.GameOn different from the legacy team apps.",
      columnFeature: "Feature",
      columnUs: "Vote.GameOn",
      columnThem: "Legacy apps",
      rows: [
        "No player app required",
        "Works inside WhatsApp & Telegram",
        "Free for coaches",
        "Privacy-first (no tracking)",
        "Live weather on the RSVP card",
        "Co-parent support out of the box",
      ],
      footnote: "Comparison reflects general product positioning of mainstream team-management apps as of 2025.",
    },
    finalCta: {
      heading: "Stop chasing replies.",
      headlineHighlight: "Start the season.",
      subhead: "Free for coaches. No card needed. Set up your first team in under two minutes.",
      cta: "Create your team — free",
      footnote: "No app for players. Ever.",
    },
  },
  es: {
    seo: {
      title: "Asistencia que funciona dentro de WhatsApp — Vote.GameOn",
      description:
        "Confirmación de asistencia sin fricción para entrenadores amateurs. Sin app, sin login, vive dentro de WhatsApp y Telegram. Privado y listo para co-padres.",
    },
    srHeading: "Asistencia que funciona dentro de WhatsApp — sin app y sin login",
    hero: {
      eyebrow: "La asistencia sin fricción",
      headline: "Asistencia que sí funciona.",
      headlineHighlight: "Sin app. Sin login. Sin perseguir respuestas.",
      subhead:
        "Pensada para WhatsApp. Pegas un enlace y recibes respuestas en segundos. Hecha para entrenadores amateurs, padres y jugadores que solo quieren que el día del partido fluya.",
      cta: "Crear un equipo gratis",
      seeHow: "Cómo funciona",
      subLinks: {
        vote: "Ver la votación del jugador →",
        rsvp: "Ver cómo funciona RSVP →",
        rpe: "Ver el monitoreo de carga (RPE) →",
        feedback: "Ver la evaluación del entrenador →",
      },
      responded: "14 de 16 respondieron",
      worksWith: "Comparte al chat de tu equipo",
    },
    problem: {
      eyebrow: "¿Te suena conocido?",
      cards: [
        { title: "Respuestas perdidas en el chat", body: "Pasas 200 memes para poder contar los que vienen." },
        { title: "Apps que ningún padre instalará", body: "Otra cuenta más. Otra contraseña olvidada." },
        { title: "Mensajes a las once de la noche", body: "Entrenadores preguntando '¿vienes el sábado?' en plena semana de colegio." },
      ],
    },
    howItWorks: {
      heading: "Tres pasos. Cero fricción.",
      subhead: "Desde que el entrenador crea el partido hasta que cada padre responde — en menos de un minuto.",
      steps: [
        { badge: "10 segundos", title: "El entrenador crea el partido", body: "Rival, hora, lugar. El enlace se genera automáticamente." },
        { badge: "1 mensaje", title: "Pégalo en WhatsApp o Telegram", body: "Aparece una vista previa con el nombre del equipo, la hora y el botón para confirmar." },
        { badge: "1 toque", title: "Los jugadores tocan Voy / No voy", body: "Sin cuenta. Sin descargas. Solo un toque. El entrenador ve los resultados al instante." },
      ],
    },
    flowVisual: {
      heading: "Todo el flujo, en una sola pantalla",
      subhead: "Del mensaje en el chat a la asistencia confirmada — cada paso que ve el padre.",
      matchTime: "Sábado, 9:00",
      matchTitle: "Lions Sub-12 vs Eagles",
      venue: "Parque Riverside, Cancha 3",
      weather: "18 °C, parcialmente nublado",
      addCalendar: "Agregar al calendario",
      going: "Voy",
      notGoing: "No voy",
      noAccount: "Sin cuenta",
      successTitle: "¡Listo! 🎉",
      successBody: "El entrenador Sam ya ve tu respuesta. Te recordamos el día anterior.",
      successCalendar: "✅ Agregado a tu calendario",
      successCoParent: "✅ Co-padre notificado",
    },
    whatsApp: {
      coachName: "Profe Sam",
      coachMessage: "Partido el sábado — porfa confirmen 🙏",
      previewTitle: "Lions Sub-12 vs Eagles — Sáb 9:00",
      previewDescription: "Toca para confirmar. Sin login.",
      teamChat: "Chat del equipo • ahora",
      parentReply: "Listo ✅",
    },
    features: {
      heading: "Hecho para la forma en que los equipos realmente se comunican",
      subhead: "Seis detalles pequeños que juntos hacen una gran diferencia.",
      cards: [
        { title: "Cero cuentas para jugadores", body: "Sin muros de registro. Tocan el enlace, confirman y listo. Los padres lo comparten en los chats familiares." },
        { title: "Vive dentro de WhatsApp y Telegram", body: "Las vistas previas muestran hora, rival y lugar — sin salir del chat." },
        { title: "Privacidad por diseño", body: "Sin subir agendas. Sin píxeles de seguimiento. Los padres controlan quién puede responder por su hijo." },
        { title: "Clima y direcciones en vivo", body: "Pronóstico de Open-Meteo a la hora del partido y navegación con un toque a Google Maps, integrados en cada tarjeta." },
        { title: "Agregar al calendario", body: "Google Calendar, descarga .ics o un calendario en vivo para padres que se actualiza si el partido cambia." },
        { title: "Pensado para co-padres", body: "Hasta dos tutores por niño. Cualquiera puede responder — invita al otro padre con un toque." },
      ],
    },
    safeguards: {
      eyebrow: "Resguardos",
      heading: "Sin fricción ",
      headingHighlight: "nunca",
      headingTail: " significa descuidado.",
      cards: [
        { title: "Bloqueado al inicio del partido", body: "Cuando comienza el partido, las respuestas se congelan. Queda un registro completo para el entrenador." },
        { title: "Gestionado por los padres", body: "Cuando un niño tiene 2 tutores activos, solo ellos pueden responder. A los desconocidos se les bloquea con buen tono." },
        { title: "Bloques de ausencia", body: "Los padres marcan vacaciones o lesión una vez — los partidos afectados se marcan automáticamente como No voy. Sin perseguir respuestas." },
      ],
    },
    comparison: {
      heading: "Por qué los entrenadores se cambian",
      subhead: "Qué hace diferente a Vote.GameOn frente a las apps de equipo de toda la vida.",
      columnFeature: "Característica",
      columnUs: "Vote.GameOn",
      columnThem: "Apps tradicionales",
      rows: [
        "No requiere app para los jugadores",
        "Funciona dentro de WhatsApp y Telegram",
        "Gratis para entrenadores",
        "Privacidad por diseño (sin tracking)",
        "Clima en vivo en la tarjeta de asistencia",
        "Soporte para co-padres listo de fábrica",
      ],
      footnote: "La comparación refleja el posicionamiento general de las apps de gestión de equipos más usadas a 2025.",
    },
    finalCta: {
      heading: "Deja de perseguir respuestas.",
      headlineHighlight: "Empieza la temporada.",
      subhead: "Gratis para entrenadores. Sin tarjeta. Arma tu primer equipo en menos de dos minutos.",
      cta: "Crea tu equipo — gratis",
      footnote: "Sin app para los jugadores. Nunca.",
    },
  },
  "pt-BR": {
    seo: {
      title: "Confirmação de presença que funciona no WhatsApp — Vote.GameOn",
      description:
        "Confirmação de presença sem atrito para treinadores de base. Sem app, sem login, vive dentro do WhatsApp e Telegram. Privacidade em primeiro lugar, pronto para co-responsáveis.",
    },
    srHeading: "Confirmação de presença que funciona no WhatsApp — sem app, sem login",
    hero: {
      eyebrow: "A presença sem atrito",
      headline: "Uma confirmação que funciona de verdade.",
      headlineHighlight: "Sem app. Sem login. Sem cobrança.",
      subhead:
        "Feita para o WhatsApp. Você cola um link e recebe respostas em segundos. Pensada para treinadores, pais e jogadores que só querem que o dia do jogo flua.",
      cta: "Criar um time grátis",
      seeHow: "Como funciona",
      subLinks: {
        vote: "Ver a votação dos jogadores →",
        rsvp: "Ver como funciona a presença →",
        rpe: "Ver o monitoramento de carga (PSE) →",
        feedback: "Ver a avaliação do treinador →",
      },
      responded: "14 de 16 responderam",
      worksWith: "Compartilha no chat do seu time",
    },
    problem: {
      eyebrow: "Soa familiar?",
      cards: [
        { title: "Respostas perdidas no grupo", body: "Você passa por 200 figurinhas só para contar quem vai." },
        { title: "Apps que nenhum pai instala", body: "Mais um login. Mais uma senha esquecida." },
        { title: "Mensagens às 23h", body: "Treinadores cobrando 'você vem sábado?' em plena semana de aula." },
      ],
    },
    howItWorks: {
      heading: "Três passos. Zero atrito.",
      subhead: "Do treinador criando o jogo até cada pai responder — em menos de um minuto.",
      steps: [
        { badge: "10 segundos", title: "O treinador cria o jogo", body: "Adversário, horário, local. O link é gerado automaticamente." },
        { badge: "1 mensagem", title: "Cole no WhatsApp ou Telegram", body: "A pré-visualização aparece com o nome do time, o horário e o botão de presença." },
        { badge: "1 toque", title: "Os jogadores tocam Vou / Não vou", body: "Sem conta. Sem download. Só um toque. O treinador vê os resultados em tempo real." },
      ],
    },
    flowVisual: {
      heading: "O fluxo todo, numa tela só",
      subhead: "Da mensagem no chat à presença confirmada — cada passo que o pai vê.",
      matchTime: "Sábado, 9h",
      matchTitle: "Lions Sub-12 vs Eagles",
      venue: "Parque Riverside, Campo 3",
      weather: "18 °C, parcialmente nublado",
      addCalendar: "Adicionar à agenda",
      going: "Vou",
      notGoing: "Não vou",
      noAccount: "Sem conta",
      successTitle: "Pronto! 🎉",
      successBody: "O treinador Sam já vê sua resposta. Lembramos você na véspera.",
      successCalendar: "✅ Adicionado à sua agenda",
      successCoParent: "✅ Co-responsável avisado",
    },
    whatsApp: {
      coachName: "Treinador Sam",
      coachMessage: "Jogo no sábado — confirmem presença, por favor 🙏",
      previewTitle: "Lions Sub-12 vs Eagles — Sáb 9h",
      previewDescription: "Toque para confirmar. Sem login.",
      teamChat: "Grupo do time • agora",
      parentReply: "Confirmado ✅",
    },
    features: {
      heading: "Feita para o jeito que os times realmente se comunicam",
      subhead: "Seis pequenos detalhes que somam uma grande diferença.",
      cards: [
        { title: "Zero contas para os jogadores", body: "Sem cadastro. Tocam o link, confirmam, pronto. Os pais compartilham nos grupos da família." },
        { title: "Vive dentro do WhatsApp e Telegram", body: "As pré-visualizações aparecem com horário, adversário e local — sem sair do chat." },
        { title: "Privacidade em primeiro lugar", body: "Sem subir agenda. Sem pixels de rastreamento. Os pais controlam quem responde pelo filho." },
        { title: "Clima e direções ao vivo", body: "Previsão da Open-Meteo no horário do jogo e navegação com um toque pelo Google Maps, integradas em cada cartão." },
        { title: "Adicionar à agenda", body: "Google Agenda, download .ics ou um calendário ao vivo para os pais que se atualiza quando o jogo muda." },
        { title: "Pensado para co-responsáveis", body: "Até dois responsáveis por criança. Qualquer um pode confirmar — convide o outro com um toque." },
      ],
    },
    safeguards: {
      eyebrow: "Salvaguardas",
      heading: "Sem atrito ",
      headingHighlight: "nunca",
      headingTail: " significa descuido.",
      cards: [
        { title: "Bloqueado no início do jogo", body: "Quando o jogo começa, as respostas congelam. Fica um histórico completo para o treinador." },
        { title: "Gerido pelos pais", body: "Quando uma criança tem 2 responsáveis ativos, só eles podem responder. Estranhos são bloqueados com elegância." },
        { title: "Bloqueios de ausência", body: "Os pais marcam férias ou lesão uma vez — os jogos afetados ficam automaticamente como Não vou. Sem cobrança." },
      ],
    },
    comparison: {
      heading: "Por que os treinadores trocam",
      subhead: "O que diferencia o Vote.GameOn dos apps de equipe tradicionais.",
      columnFeature: "Recurso",
      columnUs: "Vote.GameOn",
      columnThem: "Apps tradicionais",
      rows: [
        "Não precisa de app para jogadores",
        "Funciona dentro do WhatsApp e Telegram",
        "Grátis para treinadores",
        "Privacidade por padrão (sem rastreamento)",
        "Clima ao vivo no cartão de presença",
        "Suporte a co-responsáveis de fábrica",
      ],
      footnote: "A comparação reflete o posicionamento geral dos apps de gestão de times mais usados em 2025.",
    },
    finalCta: {
      heading: "Pare de cobrar respostas.",
      headlineHighlight: "Comece a temporada.",
      subhead: "Grátis para treinadores. Sem cartão. Monte seu primeiro time em menos de dois minutos.",
      cta: "Crie seu time — grátis",
      footnote: "Sem app para os jogadores. Nunca.",
    },
  },
  fr: {
    seo: {
      title: "Une confirmation de présence qui marche dans WhatsApp — Vote.GameOn",
      description:
        "Confirmation de présence sans friction pour les entraîneurs amateurs. Pas d'app, pas de login, ça vit dans WhatsApp et Telegram. Respect de la vie privée, prêt pour les co-parents.",
    },
    srHeading: "La confirmation de présence qui marche dans WhatsApp — sans app ni login",
    hero: {
      eyebrow: "La confirmation sans friction",
      headline: "Une confirmation qui marche vraiment.",
      headlineHighlight: "Pas d'app. Pas de login. Pas de relance.",
      subhead:
        "Pensée pour WhatsApp. Vous collez un lien et obtenez les réponses en quelques secondes. Faite pour les entraîneurs, parents et joueurs qui veulent que le jour du match se passe simplement.",
      cta: "Créer une équipe gratuite",
      seeHow: "Voir comment ça marche",
      subLinks: {
        vote: "Voir le vote du joueur →",
        rsvp: "Voir comment marche la présence →",
        rpe: "Voir le suivi de charge (RPE) →",
        feedback: "Voir l'évaluation de l'entraîneur →",
      },
      responded: "14 sur 16 ont répondu",
      worksWith: "Partage vers le chat de votre équipe",
    },
    problem: {
      eyebrow: "Ça vous parle ?",
      cards: [
        { title: "Les réponses se perdent dans le groupe", body: "Vous faites défiler 200 mèmes pour compter les présents." },
        { title: "Des apps qu'aucun parent n'installera", body: "Encore un compte. Encore un mot de passe oublié." },
        { title: "Les rappels de 23h", body: "Les entraîneurs qui demandent 'tu viens samedi ?' un soir d'école." },
      ],
    },
    howItWorks: {
      heading: "Trois étapes. Zéro friction.",
      subhead: "De la création du match par l'entraîneur à la réponse de chaque parent — en moins d'une minute.",
      steps: [
        { badge: "10 secondes", title: "L'entraîneur crée le match", body: "Adversaire, coup d'envoi, lieu. Le lien est généré automatiquement." },
        { badge: "1 message", title: "Collez dans WhatsApp ou Telegram", body: "L'aperçu se déploie avec le nom de l'équipe, l'horaire et le bouton de présence." },
        { badge: "1 clic", title: "Les joueurs cliquent Présent / Absent", body: "Pas de compte. Pas de téléchargement. Juste un clic. L'entraîneur voit les résultats en direct." },
      ],
    },
    flowVisual: {
      heading: "Tout le parcours, sur un seul écran",
      subhead: "Du message dans le chat à la présence confirmée — chaque étape vue par le parent.",
      matchTime: "Samedi, 9h00",
      matchTitle: "Lions U12 vs Eagles",
      venue: "Parc Riverside, terrain 3",
      weather: "18 °C, partiellement nuageux",
      addCalendar: "Ajouter à l'agenda",
      going: "Présent",
      notGoing: "Absent",
      noAccount: "Aucun compte",
      successTitle: "C'est fait ! 🎉",
      successBody: "L'entraîneur Sam voit votre réponse. Nous vous rappellerons la veille.",
      successCalendar: "✅ Ajouté à votre agenda",
      successCoParent: "✅ Co-parent prévenu",
    },
    whatsApp: {
      coachName: "Coach Sam",
      coachMessage: "Match samedi — confirmez votre présence 🙏",
      previewTitle: "Lions U12 vs Eagles — Sam 9h00",
      previewDescription: "Cliquez pour confirmer. Sans login.",
      teamChat: "Chat de l'équipe • à l'instant",
      parentReply: "OK ✅",
    },
    features: {
      heading: "Conçue pour la façon dont les équipes communiquent vraiment",
      subhead: "Six petits détails qui font une grande différence.",
      cards: [
        { title: "Aucun compte pour les joueurs", body: "Pas d'inscription. Ils cliquent le lien, confirment, c'est tout. Les parents partagent dans les groupes familiaux." },
        { title: "Vit dans WhatsApp et Telegram", body: "Les aperçus se déploient avec l'horaire, l'adversaire et le lieu — sans quitter le chat." },
        { title: "Vie privée par défaut", body: "Pas d'import de contacts. Pas de pixels de tracking. Les parents contrôlent qui peut répondre pour leur enfant." },
        { title: "Météo et itinéraire en direct", body: "Prévisions Open-Meteo à l'heure du match et navigation Google Maps en un clic, intégrées à chaque carte." },
        { title: "Ajouter à l'agenda", body: "Google Agenda, téléchargement .ics ou un calendrier parent en direct qui se met à jour quand le match change." },
        { title: "Pensé pour les co-parents", body: "Jusqu'à deux responsables par enfant. L'un ou l'autre peut confirmer — invitez le second en un clic." },
      ],
    },
    safeguards: {
      eyebrow: "Garde-fous",
      heading: "Sans friction ",
      headingHighlight: "ne veut jamais",
      headingTail: " dire négligent.",
      cards: [
        { title: "Verrouillé au coup d'envoi", body: "Quand le match commence, les réponses se figent. L'historique complet est conservé pour l'entraîneur." },
        { title: "Géré par les parents", body: "Dès qu'un enfant a 2 responsables actifs, eux seuls peuvent répondre. Les inconnus sont bloqués avec tact." },
        { title: "Blocs d'absence", body: "Les parents marquent vacances ou blessure une fois — les matchs concernés passent automatiquement en Absent. Plus de relance." },
      ],
    },
    comparison: {
      heading: "Pourquoi les entraîneurs changent",
      subhead: "Ce qui distingue Vote.GameOn des applis d'équipe historiques.",
      columnFeature: "Fonctionnalité",
      columnUs: "Vote.GameOn",
      columnThem: "Applis historiques",
      rows: [
        "Pas d'app exigée pour les joueurs",
        "Fonctionne dans WhatsApp et Telegram",
        "Gratuit pour les entraîneurs",
        "Vie privée par défaut (sans tracking)",
        "Météo en direct sur la carte de présence",
        "Co-parents pris en charge nativement",
      ],
      footnote: "La comparaison reflète le positionnement général des applis grand public de gestion d'équipe en 2025.",
    },
    finalCta: {
      heading: "Arrêtez de relancer.",
      headlineHighlight: "Lancez la saison.",
      subhead: "Gratuit pour les entraîneurs. Sans carte bancaire. Créez votre première équipe en moins de deux minutes.",
      cta: "Créez votre équipe — gratuit",
      footnote: "Aucune app pour les joueurs. Jamais.",
    },
  },
  ja: {
    seo: {
      title: "WhatsApp/LINEでそのまま使える出欠確認 — Vote.GameOn",
      description:
        "草の根フットボールのコーチのための摩擦ゼロの出欠確認。アプリ・ログイン不要で、WhatsAppやTelegramの中でそのまま完結。プライバシー重視、共同保護者にも対応。",
    },
    srHeading: "WhatsApp/LINEの中で動く出欠確認 — アプリ不要、ログイン不要",
    hero: {
      eyebrow: "摩擦ゼロの出欠確認",
      headline: "本当に機能する出欠確認。",
      headlineHighlight: "アプリなし。ログインなし。催促なし。",
      subhead:
        "WhatsApp/LINE前提の設計。リンクを1本貼るだけで数秒で返信が集まります。試合当日をスムーズにしたいコーチ・保護者・選手のために作りました。",
      cta: "無料でチームを作る",
      seeHow: "仕組みを見る",
      subLinks: {
        vote: "選手間投票を見る →",
        rsvp: "出欠確認の仕組みを見る →",
        rpe: "運動負荷トラッキングを見る →",
        feedback: "コーチ評価の仕組みを見る →",
      },
      responded: "16人中14人が回答",
      worksWith: "チームのチャットアプリにそのまま共有",
    },
    problem: {
      eyebrow: "こんな経験、ありませんか？",
      cards: [
        { title: "グループチャットで返信が埋もれる", body: "200個のスタンプをスクロールしながら「行く」を数えるハメに。" },
        { title: "誰もインストールしないアプリ", body: "また新しいログイン。また忘れるパスワード。" },
        { title: "夜11時のリマインド", body: "平日夜にコーチが「土曜来る？」と一人ひとりに聞き回る。" },
      ],
    },
    howItWorks: {
      heading: "3ステップ。摩擦ゼロ。",
      subhead: "コーチが試合を作成してから、全保護者が返信するまで — 1分以内。",
      steps: [
        { badge: "10秒", title: "コーチが試合を作成", body: "対戦相手・キックオフ・場所を入れると、リンクが自動生成されます。" },
        { badge: "メッセージ1通", title: "WhatsAppやLINEに貼るだけ", body: "チーム名・時間・出欠ボタンがリッチプレビューで展開されます。" },
        { badge: "ワンタップ", title: "選手が「行く／行かない」をタップ", body: "アカウント不要、ダウンロード不要。タップするだけ。コーチにはリアルタイムで反映されます。" },
      ],
    },
    flowVisual: {
      heading: "全体の流れを1画面で",
      subhead: "チャットのメッセージから出欠確定まで — 保護者が実際に見る画面。",
      matchTime: "土曜 9:00",
      matchTitle: "Lions U12 対 Eagles",
      venue: "リバーサイド公園 3番グラウンド",
      weather: "18℃、晴れ時々曇り",
      addCalendar: "カレンダーに追加",
      going: "行く",
      notGoing: "行かない",
      noAccount: "アカウント不要",
      successTitle: "受付完了！🎉",
      successBody: "サムコーチにあなたの回答が届きました。前日にリマインドします。",
      successCalendar: "✅ カレンダーに追加しました",
      successCoParent: "✅ 共同保護者にも通知しました",
    },
    whatsApp: {
      coachName: "サムコーチ",
      coachMessage: "土曜に試合です — 出欠のご返信お願いします🙏",
      previewTitle: "Lions U12 対 Eagles — 土 9:00",
      previewDescription: "タップして出欠回答。ログイン不要。",
      teamChat: "チームチャット・たった今",
      parentReply: "回答しました✅",
    },
    features: {
      heading: "チームが実際にコミュニケーションする形に合わせて設計",
      subhead: "小さな工夫が6つ、合わさって大きな違いになります。",
      cards: [
        { title: "選手はアカウント不要", body: "会員登録は一切なし。リンクをタップして回答するだけ。保護者は家族のチャットで共有できます。" },
        { title: "WhatsApp・Telegramの中で完結", body: "リッチリンクのプレビューにキックオフ時間・対戦相手・場所が展開され、チャットを離れる必要がありません。" },
        { title: "プライバシー・ファースト設計", body: "連絡先のアップロード不要。トラッキングピクセルなし。子どもの代理回答は保護者が管理します。" },
        { title: "リアルタイムの天気と経路", body: "Open-Meteoによるキックオフ時刻の天気予報と、Google Mapsへのワンタップ経路案内を各カードに標準搭載。" },
        { title: "カレンダーに追加", body: "Googleカレンダー、.icsダウンロード、または試合変更時に自動更新される保護者向けライブカレンダーに対応。" },
        { title: "共同保護者に配慮", body: "1人の子に対して保護者は最大2名まで。どちらでも回答可能 — もう一方の保護者もワンタップで招待できます。" },
      ],
    },
    safeguards: {
      eyebrow: "安全性",
      heading: "摩擦ゼロは、",
      headingHighlight: "決して",
      headingTail: " 「ずさん」を意味しません。",
      cards: [
        { title: "キックオフでロック", body: "試合が始まると回答は締め切られます。監査ログはコーチ側に完全に残ります。" },
        { title: "保護者が管理", body: "有効な保護者が2名登録されると、その2名しか回答できません。第三者は丁寧にブロックされます。" },
        { title: "不在期間ブロック", body: "保護者が休暇や怪我を1回登録すれば、対象試合は自動で「行かない」に。フォローアップ不要。" },
      ],
    },
    comparison: {
      heading: "なぜコーチが乗り換えるのか",
      subhead: "従来のチーム管理アプリとVote.GameOnの違い。",
      columnFeature: "機能",
      columnUs: "Vote.GameOn",
      columnThem: "従来型アプリ",
      rows: [
        "選手用アプリ不要",
        "WhatsApp・Telegramの中で動く",
        "コーチは無料",
        "プライバシー・ファースト（トラッキングなし）",
        "出欠カードにライブ天気",
        "共同保護者機能を標準搭載",
      ],
      footnote: "主要なチーム管理アプリの一般的な位置づけを2025年時点で比較。",
    },
    finalCta: {
      heading: "催促は、もうやめよう。",
      headlineHighlight: "シーズンを始めよう。",
      subhead: "コーチは無料。カード登録不要。最初のチームを2分以内でセットアップ。",
      cta: "無料でチームを作る",
      footnote: "選手用アプリは、これからも一切なし。",
    },
  },
};
